import React, { useRef, useEffect } from "react";
import { Transition } from "../../../utils";
import { PaziaModalSize } from "./PaziaModal";

export interface PaziaModalBlankProps {
  children: React.ReactNode;
  id: string;
  modalOpen: boolean;
  style?: React.CSSProperties;
  size?: PaziaModalSize;
  onCloseRequest: () => void;
  className?: string;
}

const widthClassess: Record<PaziaModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  "90%": "max-w-90%",
  full: "w-[calc(100vw-48px)] h-[calc(100vh-48px)]",
};
export const PaziaModalBlank = ({
  children,
  id,
  modalOpen,
  style,
  onCloseRequest,
  size = "md",
  className,
}: PaziaModalBlankProps) => {
  const modalContent = useRef(null);
  const mousedownOnOverlay = useRef(false);

  // close on click outside
  useEffect(() => {
    const mousedownHandler = ({ target }: MouseEvent) => {
      mousedownOnOverlay.current = (target as Element).classList.contains("pazia-modal-overlay");
    };
    const clickHandler = ({ target }: MouseEvent) => {
      if (
        (target as Element).classList.contains("pazia-modal-overlay") &&
        mousedownOnOverlay.current
      ) {
        onCloseRequest();
      }
    };
    document.addEventListener("mousedown", mousedownHandler);
    document.addEventListener("click", clickHandler);
    return () => {
      document.removeEventListener("mousedown", mousedownHandler);
      document.removeEventListener("click", clickHandler);
    };
  });

  // close if the esc key is pressed
  useEffect(() => {
    //@ts-ignore
    const keyHandler = ({ keyCode }) => {
      if (!modalOpen || keyCode !== 27) return;
      onCloseRequest();
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  return (
    <>
      <Transition
        className="fixed inset-0 bg-slate-900 bg-opacity-30 z-50 transition-opacity"
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      {/* Modal dialog */}
      <Transition
        id={id}
        className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center px-4 sm:px-6 pazia-modal-overlay"
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
      >
        <div
          style={style}
          ref={modalContent}
          className={`bg-white dark:bg-slate-800 shadow-lg overflow-y-auto border border-slate-200 dark:border-slate-700 ${
            size === "full" ? "" : "max-h-[90dvh] w-full"
          } ${widthClassess[size]} z-30 ${className ?? ""}`}
        >
          {children}
        </div>
      </Transition>
    </>
  );
};
